import React, {
    useState, useEffect, useRef,
} from 'react';

import { useRecoilValue } from 'recoil';
import { Tooltip } from 'flowbite';

import CloseIcon from '@/components/commons/icons/CloseIcon';
import { onboardingAtom, defaultBlankUserAtom, onboardingRemindLater } from '../with_auth/State';

// This step content holds the content that will be displayed
// inside the tooltip for the various step (1,2,3 etc)
const stepContent = {
    agenda: {
        2: 'These are your starter agendas. You can Edit them, or Add a new agenda.',
        3: 'Customize your agenda, then send a test to see if it works as expected. (You do not have to save changes first.)',
        4: 'Your new agenda has been created! Send Now to generate the first one.',
    },
    alert: {
        1: 'Let\'s create your first alert, so you can see just how easy this is.',
        2: 'The "//" marker attaches to your text string (Ex.: "// Zoom").',
    },
};

interface Props {
    readonly step: number;
    readonly placement?: 'top' | 'right' | 'bottom' | 'left';
}

function OnboardingTooltip({
    step,
    placement = 'top',
}: Props) {
    const ref = useRef(null);
    const classRef = useRef(null);
    const onboardingData = useRecoilValue(onboardingAtom);
    const user = useRecoilValue(defaultBlankUserAtom);
    const remindLater = useRecoilValue(onboardingRemindLater);
    const { type } = onboardingData;

    const [open, setOpen] = useState(true);

    const id = `[data-onboarding=${type}-step-${step}]`;

    useEffect(() => {
        if (ref.current && step === onboardingData[type]) {
            // If we can't find the element don't show the tip
            const $triggerEl: HTMLElement = document.querySelector(id);
            if ($triggerEl) {
                classRef.current = new Tooltip(ref.current, $triggerEl, {
                    placement,
                    triggerType: 'none',
                    onHide() {
                        // for some reason there's almost no way to tell
                        // these tooltip libraries to just STAY OPEN programmatically
                        // almost ...
                        if (open) {
                            classRef.current.show();
                        }
                    },
                });
                if (open) {
                    classRef.current.show();
                } else {
                    classRef.current.hide();
                }
            }
        }
    });

    const getContent = () => stepContent[type][onboardingData[type]];

    // If this step doesn't match our state's step, we're not rendering this tooltip
    if (step !== onboardingData[type]) {
        return null;
    }

    const hide = (type === 'agenda' && user.hasCreatedAgendas)
        || (type === 'alert' && user.hasCreatedAlerts)
        || remindLater;

    if (hide) {
        return null;
    }

    return (
        <div ref={ref} role="tooltip" className="absolute z-10 max-w-[350px] invisible inline-block px-3 py-2 text-sm font-medium text-cirrusWhite transition-opacity duration-300 bg-black rounded-lg shadow-sm opacity-0 tooltip">
            <div className="flex flex-row items-center justify-center">
                <span>{getContent()}</span>
                <button
                    className="text-cirrusWhite ml-3"
                    type="button"
                    aria-label="Close"
                    onClick={() => {
                        setOpen(false);
                    }}
                >
                    <CloseIcon className="ml-0 mr-0 h-2 w-2 text-white" />
                </button>
            </div>
            <div className="tooltip-arrow" data-popper-arrow />
        </div>
    );
}

export default OnboardingTooltip;
